// src/components/StatusChips.tsx
import React, { useMemo } from 'react';

export type ChipMeta = { key: string; label: string; bg: string; fg: string };

type Props = {
  /** Набор статусов (ключ, подпись и цвета) */
  metas: ReadonlyArray<ChipMeta>;
  /** Счётчики по ключам статусов */
  counts: Record<string, number>;
  /** Активные статусы (подсветка/состояние) */
  activeKeys?: ReadonlyArray<string> | ReadonlySet<string>;
  /** Клик по чипу */
  onToggle: (key: string) => void;
  /** Скрывать чипы с нулём */
  hideZero?: boolean;
  /** Проброс класса на контейнер */
  className?: string;
};

const asSet = (v?: ReadonlyArray<string> | ReadonlySet<string>) =>
  v instanceof Set ? v : new Set(v ?? []);

const StatusChips: React.FC<Props> = ({
  metas,
  counts,
  activeKeys,
  onToggle,
  hideZero = true,
  className,
}) => {
  const active = useMemo(() => asSet(activeKeys), [activeKeys]);
  const items = useMemo(
    () => metas.filter(m => !hideZero || (counts[m.key] ?? 0) > 0),
    [metas, counts, hideZero]
  );

  if (!items.length) return null;

  return (
    <div className={className}>
      {items.map((m) => {
        const isActive = active.has(m.key);
        const count = counts[m.key] ?? 0;
        return (
          <span
            key={m.key}
            className={`stat-card__chip ${isActive ? 'is-active' : ''}`}
            style={{ background: m.bg, color: m.fg, cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => onToggle(m.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle(m.key);
              }
            }}
            aria-pressed={isActive}
            data-key={m.key}
            title={`${m.label}: ${count}`}
          >
            {m.label}: {count}
          </span>
        );
      })}
    </div>
  );
};

export default StatusChips;
